'use client';

import { useTranslations } from 'next-intl';

import { FormGroup, FormControl, FormHelperText, FormLabel } from '@mui/material';

import { Button } from '../button/Button';
import { Checkbox } from '../checkbox/Checkbox';

import { selectAll } from './checkboxList.util';

import type { TNameType } from '../../types/components/components.type';
import type { ICheckboxListProps } from './checkboxList.type';

export const CheckboxList = <TName extends TNameType>({
  name,
  availableValues = [],
  handleChange,
  handleBlur,
  value,
  label,
  helperText,
  className = '',
  disabled = false,
  multiple = true,
  errorMessage = '',
  selectableAll = false,
  required = [],
  color = 'default',
  sx = {},
  autoFocus = false,
  strikeSelected = false,
}: ICheckboxListProps<TName>) => {
  const t = useTranslations();

  const localChange = (itemValue: string) => {
    if (!multiple) {
      handleChange(
        { key: name, value: value.includes(itemValue) ? [] : [itemValue] },
        availableValues,
      );
    } else if (value.includes(itemValue)) {
      handleChange(
        { key: name, value: value.filter((item) => item !== itemValue) },
        availableValues,
      );
    } else {
      handleChange({ key: name, value: [...value, itemValue] }, availableValues);
    }
  };

  const isError = !!errorMessage;

  return (
    <>
      <FormControl
        autoFocus={autoFocus}
        // For faster trigger form validating
        onChange={handleBlur}
        sx={sx}
        component="fieldset"
        variant="standard"
        id={name}
        name={name}
        error={isError}
      >
        <FormLabel sx={{ marginBottom: 2, fontWeight: 600 }} aria-labelledby={name}>
          {label}
        </FormLabel>

        <FormGroup className={className}>
          {availableValues.map(({ value: itemValue, label: itemLabel, labelLong, StartIcon }) => (
            <Checkbox
              key={itemValue}
              name={`Checkbox-${itemValue}`}
              label={itemLabel}
              labelLong={labelLong}
              color={color}
              handleChange={() => localChange(itemValue)}
              value={value.includes(itemValue)}
              required={required.includes(itemValue)}
              disabled={disabled}
              strikeSelected={strikeSelected}
              StartIcon={StartIcon}
            />
          ))}
        </FormGroup>

        {(isError || helperText) && (
          <FormHelperText>{isError ? errorMessage : helperText}</FormHelperText>
        )}
      </FormControl>

      {selectableAll && (
        <Button
          variant="text"
          size="large"
          handleClick={() => {
            handleChange({ key: name, value: selectAll(availableValues) }, availableValues);
          }}
        >
          {t('labels.selectAll')}
        </Button>
      )}
    </>
  );
};
